import React from "react";
import { Alert, Button, Form, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import LanguageTabPanel from "./LanguageTabPanel";
import {
  getLanguageLabel,
  isIntlLanguageNameLocked,
  isLanguageTabPending,
  swapDefaultLanguage,
} from "../../../../utils/intlRegister";
import styles from "../index.modules.less";

const LanguageSection = ({
  form,
  languageOptions,
  portal11Status,
  selectedLanguages,
  setSelectedLanguages,
  defaultLanguage,
  setDefaultLanguage,
  activeTab,
  setActiveTab,
  onRetryLoadOptions,
  showLanguagePicker = true,
  showLanguageTabs = true,
  detailRaw = {},
  pendingTabsHighlight = false,
}) => {
  const formValues = Form.useWatch([], { form, preserve: true }) || {};
  const activeLanguageCode = activeTab || selectedLanguages[0] || "";
  const activeLanguageLabel = getLanguageLabel(languageOptions, activeLanguageCode);

  const toggleLanguage = (code) => {
    const isSelected = selectedLanguages.includes(code);

    if (isSelected) {
      if (selectedLanguages.length <= 1) {
        return;
      }

      const nextSelected = selectedLanguages.filter((item) => item !== code);
      setSelectedLanguages(nextSelected);

      if (defaultLanguage === code) {
        const nextDefault = nextSelected[0];
        swapDefaultLanguage(form, code, nextDefault);
        setDefaultLanguage(nextDefault);
        setActiveTab(nextDefault);
      } else if (activeTab === code) {
        setActiveTab(nextSelected[0]);
      }

      const extendedLang = { ...(form.getFieldValue("extendedLang") || {}) };
      delete extendedLang[code];
      form.setFieldsValue({ extendedLang });
      return;
    }

    const nextSelected = [...selectedLanguages, code];
    setSelectedLanguages(nextSelected);
    if (!defaultLanguage) {
      setDefaultLanguage(code);
    }
    setActiveTab(code);
  };

  const handleSetDefault = (code) => {
    if (!code || code === defaultLanguage) {
      return;
    }
    swapDefaultLanguage(form, defaultLanguage, code);
    setDefaultLanguage(code);
    setActiveTab(code);
  };

  if (portal11Status === "failed") {
    if (!showLanguagePicker) {
      return null;
    }
    return (
      <div className={styles.placeholder}>
        <div>语言枚举加载失败，无法配置多语言</div>
        <Button type="link" onClick={onRetryLoadOptions}>
          重试
        </Button>
      </div>
    );
  }

  if (!showLanguagePicker && !showLanguageTabs) {
    return null;
  }

  return (
    <>
      {showLanguagePicker ? (
        <Form.Item
          label="生效语言"
          htmlFor={undefined}
          required
          validateStatus={selectedLanguages.length ? "" : "error"}
          help={selectedLanguages.length ? null : "请至少选择一种生效语言"}
        >
          <div className={styles.languageTags}>
            {languageOptions.map((item) => {
              const selected = selectedLanguages.includes(item.language_code);
              return (
                <button
                  key={item.language_code}
                  type="button"
                  className={selected ? styles.langTagSelected : styles.langTagOption}
                  onClick={() => toggleLanguage(item.language_code)}
                >
                  {item.language_name || item.language_code}
                </button>
              );
            })}
          </div>
        </Form.Item>
      ) : null}

      {showLanguageTabs ? (
        selectedLanguages.length ? (
          <div className={styles.multiLangCard}>
            <div className={styles.multiLangHead}>
              <div className={styles.multiLangTitleRow}>
                <span className={styles.multiLangAccent} />
                <h3 className={styles.multiLangTitle}>多语言配置</h3>
                <Tooltip title="安装包在基础信息区配置（全语言共一份）；此处 Tab 仅配置各语言图文素材">
                  <InfoCircleOutlined className={styles.multiLangInfoIcon} />
                </Tooltip>
              </div>
              <div className={styles.multiLangTabBar}>
                {selectedLanguages.map((code) => {
                  const label = getLanguageLabel(languageOptions, code);
                  const isDefault = code === defaultLanguage;
                  const isActive = code === activeLanguageCode;
                  const pending = isLanguageTabPending(
                    formValues,
                    code,
                    defaultLanguage
                  );
                  return (
                    <button
                      key={code}
                      type="button"
                      className={[
                        styles.multiLangTab,
                        isActive ? styles.multiLangTabActive : "",
                        pending && pendingTabsHighlight
                          ? styles.multiLangTabPendingError
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => setActiveTab(code)}
                    >
                      <span className={styles.multiLangTabName}>{label}</span>
                      <span className={styles.multiLangTabBadges}>
                        {isDefault ? (
                          <span className={styles.badgeDefault}>默认</span>
                        ) : null}
                        {pending ? (
                          <span
                            className={
                              pendingTabsHighlight
                                ? `${styles.badgePending} ${styles.badgePendingError}`
                                : styles.badgePending
                            }
                          >
                            待填
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.multiLangBody}>
              <Alert
                type="info"
                showIcon
                className={styles.multiLangContextAlert}
                message={
                  <>
                    您正在配置<strong>【{activeLanguageLabel}】</strong>
                    的对应语言素材。
                  </>
                }
              />

              {selectedLanguages.map((code) => {
                const isDefault = code === defaultLanguage;
                const isActive = code === activeLanguageCode;
                return (
                  <div
                    key={code}
                    className={isActive ? "" : styles.multiLangPanelHidden}
                  >
                    <LanguageTabPanel
                      form={form}
                      languageCode={code}
                      languageLabel={getLanguageLabel(languageOptions, code)}
                      isDefault={isDefault}
                      onSetDefault={handleSetDefault}
                      nameDisabled={isIntlLanguageNameLocked(detailRaw, code, {
                        languageOptions,
                      })}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={styles.placeholder}>请选择至少一种生效语言后配置多语言素材</div>
        )
      ) : null}
    </>
  );
};

export default LanguageSection;
